import { useState, useEffect, useMemo, useCallback } from 'react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import DOMPurify from 'dompurify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Plus, Briefcase, Users, Eye, CheckCircle, FileText, MessageSquare, Download, Printer, Loader2, X, MapPin, Mail, Phone, User, Heart, Send, Clock, Search, ArrowRight, Newspaper, BookOpen, ExternalLink } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AwardsDisplay } from '@/components/AwardsDisplay';
import { WorkforceSnapshot } from '@/components/employer/WorkforceSnapshot';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Regional service descriptions may contain rich HTML — strip to plain text for compact previews.
const toPreviewText = (s) => DOMPurify.sanitize(s || '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).replace(/\s+/g, ' ').trim();
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

function EmployerDashboard() {
  const [user, setUser] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidatesAll, setCandidatesAll] = useState([]);
  const [savedCandidates, setSavedCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [appSort, setAppSort] = useState('action_needed');
  const [appQuery, setAppQuery] = useState('');
  const [loading, setLoading] = useState(true);
  // Region-owned content
  const [branding, setBranding] = useState(null);   // { name, logo_url }
  const [latestNews, setLatestNews] = useState([]);
  const [resources, setResources] = useState([]);
  const [guides, setGuides] = useState([]);            // global Employer Guides (content_type=resource)
  const [workforceUpdate, setWorkforceUpdate] = useState(null); // most recent global news (compact)
  const [conversationsCount, setConversationsCount] = useState(0); // real unread threads (existing API)
  const [regionalContact, setRegionalContact] = useState(null); // { region_name, contact }
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = ['jobs', 'applications', 'candidates'].includes(params.get('tab')) ? params.get('tab') : 'jobs';
  // Job Command Center drill-down scope (explicit, whitelisted params only).
  const jobFilter = params.get('job') || '';
  const stageFilter = params.get('stage') || '';
  const STAGE_LABELS = { needs_response: 'Needs Response', interviewing: 'Interviewing', offers: 'Offers', hired: 'Hired' };
  
  // Application viewer state
  const [viewingApplication, setViewingApplication] = useState(null);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [loadingApplication, setLoadingApplication] = useState(false);
  const [downloadingResumePdf, setDownloadingResumePdf] = useState(false);
  const [downloadingCoverPdf, setDownloadingCoverPdf] = useState(false);
  const [activeDocTab, setActiveDocTab] = useState('resume');

  // Smart View Full Profile state (for Saved Candidates)
  const [checkingAccess, setCheckingAccess] = useState(null); // candidate user_id being checked
  const [savedResumeViewerOpen, setSavedResumeViewerOpen] = useState(false);
  const [savedResumeData, setSavedResumeData] = useState(null);
  const [savedResumeCandidate, setSavedResumeCandidate] = useState(null);
  const [loadingSavedResume, setLoadingSavedResume] = useState(false);
  const [savedResumeDocTab, setSavedResumeDocTab] = useState('resume');
  const [savedAccessRequestOpen, setSavedAccessRequestOpen] = useState(false);
  const [savedAccessNote, setSavedAccessNote] = useState('');
  const [sendingSavedRequest, setSendingSavedRequest] = useState(false);
  const [savedAccessCandidate, setSavedAccessCandidate] = useState(null);

  const fetchData = async () => {
    try {
      const [userRes, employerRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/employer/profile`, { withCredentials: true })
      ]);
      
      setUser(userRes.data);
      setEmployer(employerRes.data);

      if (employerRes.data?.status === 'approved') {
        const [jobsRes, candidatesRes, applicationsRes, favoritesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/jobs/my-posts/list`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/candidates/search`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/jobs/my-posts/applications`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/candidates/favorites/list`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setJobs(jobsRes.data);
        setCandidates(candidatesRes.data.slice(0, 10));
        setCandidatesAll(candidatesRes.data || []);
        setAllApps(applicationsRes.data || []);
        // When arriving under a Command Center scope, the scoped effect owns the display list.
        if (!params.get('job') && !params.get('stage')) {
          setApplications(applicationsRes.data || []);
        }
        setSavedCandidates(favoritesRes.data || []);

        // Region-owned content (branding + news + resources + local contact) + global content
        // (Employer Guides + Workforce Updates). All fail-soft — never block the dashboard.
        const rid = employerRes.data?.region_id;
        const [brand, news, res, contact, gGuides, gNews] = await Promise.all([
          rid ? axios.get(`${BACKEND_URL}/api/regions/${rid}/branding`, { withCredentials: true }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          axios.get(`${BACKEND_URL}/api/regional/news`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/regional/resources`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/regional/contact`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${BACKEND_URL}/api/platform/content?type=resource`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/platform/content?type=news`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setBranding(brand.data);
        setLatestNews(news.data || []);
        setResources(res.data || []);
        setRegionalContact(contact.data);
        setGuides(gGuides.data || []);
        setWorkforceUpdate((gNews.data || [])[0] || null);
        axios.get(`${BACKEND_URL}/api/messages/unread-count`, { withCredentials: true })
          .then((r) => setConversationsCount(r.data?.unread_threads || 0)).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to load employer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Compute per-job application counts from the full (unscoped) list so drill-downs never skew counts
  const appCountByJob = useMemo(() => {
    const map = {};
    for (const app of allApps) {
      map[app.job_id] = (map[app.job_id] || 0) + 1;
    }
    return map;
  }, [allApps]);

  const activeJobs = useMemo(() => jobs.filter(j => j.status === 'active'), [jobs]);
  // Distinct semantics (canonical job_applications): applications = candidate↔job records,
  // applicants = unique people, needs_response = applications awaiting employer action.
  const needsResponseCount = useMemo(() => allApps.filter(a => a.needs_response).length, [allApps]);
  const uniqueApplicantCount = useMemo(() => new Set(allApps.map(a => a.applicant_id)).size, [allApps]);

  // Re-fetch applications from the canonical endpoint with server-side sort + search + optional
  // job/stage scope (Job Command Center drill-down).
  const fetchApplications = useCallback(async (sort, q, job, stage) => {
    try {
      const params = new URLSearchParams();
      if (sort) params.append('sort', sort);
      if (q && q.trim()) params.append('q', q.trim());
      if (job) params.append('job', job);
      if (stage) params.append('stage', stage);
      const res = await axios.get(`${BACKEND_URL}/api/jobs/my-posts/applications?${params.toString()}`, { withCredentials: true });
      setApplications(res.data || []);
    } catch { /* keep existing */ }
  }, []);

  // Keep the Applications list in sync with the active job/stage scope + tab.
  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications(appSort, appQuery, jobFilter, stageFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilter, stageFilter, activeTab]);

  const scopedJobTitle = useMemo(() => {
    if (!jobFilter) return '';
    return jobs.find(j => j.job_id === jobFilter)?.title
      || applications.find(a => a.job_id === jobFilter)?.job_title
      || 'this role';
  }, [jobFilter, jobs, applications]);

  const clearScope = () => setParams({ tab: 'applications' });

  // Phase 1B — "In your region" region context line.
  // Reuses already-loaded candidate search results; no new API call.
  // Counts candidates with a known distance ≤ 25 mi from employer base.
  const candidatesInRegionCount = useMemo(() => {
    if (!candidatesAll?.length) return 0;
    return candidatesAll.filter((c) => {
      const d = c?.distance_miles;
      return typeof d === 'number' && isFinite(d) && d <= 25;
    }).length;
  }, [candidatesAll]);

  const handleMarkHired = async (applicationId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/applications/${applicationId}/mark-hired`,
        {},
        { withCredentials: true }
      );
      toast.success('Candidate marked as hired!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as hired');
    }
  };

  // Move a native-job applicant through the workflow. Updates the ONE canonical
  // application + appends a truthful employer-origin lifecycle event (seeker sees it).
  const [dispatching, setDispatching] = useState(null);
  const handleDisposition = async (applicationId, action) => {
    setDispatching(`${applicationId}:${action}`);
    try {
      await axios.put(
        `${BACKEND_URL}/api/employer/applications/${applicationId}/disposition`,
        { action },
        { withCredentials: true }
      );
      const labels = { reviewing: 'Marked as reviewing', interview: 'Moved to Interviewing',
        offer: 'Offer made', hired: 'Candidate hired', not_selected: 'Marked not selected' };
      toast.success(labels[action] || 'Updated');
      await fetchData();
      // Stay within the current job/stage scope after acting.
      if (jobFilter || stageFilter) await fetchApplications(appSort, appQuery, jobFilter, stageFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update applicant');
    } finally {
      setDispatching(null);
    }
  };

  // View Application handlers
  const handleViewApplication = async (applicationId) => {
    setViewingApplication(applicationId);
    setLoadingApplication(true);
    setActiveDocTab('resume');
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/employer/applications/${applicationId}`,
        { withCredentials: true }
      );
      setApplicationDetails(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load application');
      setViewingApplication(null);
    } finally {
      setLoadingApplication(false);
    }
  };

  const handleCloseViewer = () => {
    setViewingApplication(null);
    setApplicationDetails(null);
  };

  const handleDownloadResumePdf = async () => {
    if (!viewingApplication) return;
    setDownloadingResumePdf(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/employer/applications/${viewingApplication}/resume/pdf`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Resume_${applicationDetails?.applicant_name?.replace(/\s+/g, '_') || 'Applicant'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Resume downloaded');
    } catch (error) {
      toast.error('Failed to download resume');
    } finally {
      setDownloadingResumePdf(false);
    }
  };

  const handleDownloadCoverPdf = async () => {
    if (!viewingApplication) return;
    setDownloadingCoverPdf(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/employer/applications/${viewingApplication}/cover-letter/pdf`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CoverLetter_${applicationDetails?.applicant_name?.replace(/\s+/g, '_') || 'Applicant'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Cover letter downloaded');
    } catch (error) {
      toast.error('Failed to download cover letter');
    } finally {
      setDownloadingCoverPdf(false);
    }
  };

  const handlePrintResume = async () => {
    if (!viewingApplication) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/employer/applications/${viewingApplication}/resume/pdf`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Print failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      };
      
      toast.success('Print dialog opened');
    } catch (error) {
      toast.error('Failed to print');
    }
  };

  const handlePrintCover = async () => {
    if (!viewingApplication) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/employer/applications/${viewingApplication}/cover-letter/pdf`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Print failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      };
      
      toast.success('Print dialog opened');
    } catch (error) {
      toast.error('Failed to print');
    }
  };

  // ===== UN-FAVORITE (Saved Candidates) =====
  const handleUnfavorite = async (candidateUserId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/candidates/${candidateUserId}/favorite`, { withCredentials: true });
      setSavedCandidates(prev => prev.filter(c => c.user_id !== candidateUserId));
      toast.success('Removed from saved candidates');
    } catch (error) {
      toast.error('Failed to remove candidate');
    }
  };

  // ===== SMART VIEW FULL PROFILE (Saved Candidates) =====
  const handleSavedViewProfile = async (candidate) => {
    setCheckingAccess(candidate.user_id);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/messages/resume-access/check/${candidate.user_id}`,
        { withCredentials: true }
      );

      if (data.has_access) {
        setSavedResumeCandidate(candidate);
        setSavedResumeDocTab('resume');
        setLoadingSavedResume(true);
        setSavedResumeViewerOpen(true);
        try {
          const resumeRes = await axios.get(
            `${BACKEND_URL}/api/messages/resume-access/${data.access_request_id}/resume`,
            { withCredentials: true }
          );
          setSavedResumeData(resumeRes.data);
        } catch (err) {
          toast.error('Failed to load resume');
          setSavedResumeViewerOpen(false);
        } finally {
          setLoadingSavedResume(false);
        }
      } else if (data.pending) {
        toast.info('Your access request is pending approval from this candidate.', { duration: 4000 });
      } else {
        setSavedAccessCandidate(candidate);
        setSavedAccessNote('');
        setSavedAccessRequestOpen(true);
      }
    } catch (err) {
      toast.error('Failed to check access status');
    } finally {
      setCheckingAccess(null);
    }
  };

  const handleSendSavedAccessRequest = async () => {
    if (!savedAccessCandidate) return;
    setSendingSavedRequest(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/messages/resume-access/request-direct`,
        { candidate_user_id: savedAccessCandidate.user_id, note: savedAccessNote.trim() || null },
        { withCredentials: true }
      );
      toast.success('Access request sent! The candidate will be notified.');
      setSavedAccessRequestOpen(false);
      setSavedAccessNote('');
      setSavedAccessCandidate(null);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('already')) {
        toast.info('A request is already pending for this candidate.');
        setSavedAccessRequestOpen(false);
      } else {
        toast.error(detail || 'Failed to send access request');
      }
    } finally {
      setSendingSavedRequest(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </Layout>
    );
  }

  if (!employer) {
    return (
      <Layout user={user}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Register as Employer</h1>
          <p className="text-slate-600 mb-8">You need to register as an employer to access this dashboard.</p>
          <Button
            onClick={() => navigate('/employer/register')}
            className="bg-brand hover:bg-brand-hover text-white h-12 px-8"
          >
            Register Now
          </Button>
        </div>
      </Layout>
    );
  }

  if (employer.status === 'pending') {
    return (
      <Layout user={user}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="p-8 border border-amber-200 bg-amber-50 text-center">
            <h1 className="text-2xl font-bold text-amber-900 mb-4">Pending Approval</h1>
            <p className="text-amber-800">
              Your employer account is pending approval from our team. 
              You&apos;ll be notified once your account is approved.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  // Render Resume Preview
  const renderResumePreview = () => {
    if (!applicationDetails?.has_resume || !applicationDetails?.resume_data) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="w-16 h-16 text-slate-300 mb-4" />
          <p className="text-slate-600 font-medium">Resume not available yet</p>
          <p className="text-sm text-slate-500 mt-1">The candidate has not completed their resume</p>
        </div>
      );
    }

    const resume = applicationDetails.resume_data;
    const pi = resume.personal_info || {};

    // Core Competencies
    const coreComps = (() => {
      if (!resume.military_roles?.length) return [];
      const all = [];
      for (const role of resume.military_roles) {
        for (const item of (role.selected_abilities || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
        for (const item of (role.selected_skills || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
        for (const item of (role.selected_knowledge || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
      }
      const seen = new Set();
      return all.filter(c => { const l = c.toLowerCase().trim(); if (seen.has(l)) return false; seen.add(l); return true; }).slice(0, 15);
    })();

    const parseBullets = (text) => {
      if (!text) return [];
      const lower = text.toLowerCase();
      if (lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') ||
          lower.includes('rate_limit_exceeded') || (text.match(/<[a-zA-Z/][^>]*>/g) || []).length > 3) {
        return [];
      }
      return text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#')).map(l => l.replace(/^[•\-\*▪◦●]\s*/, ''));
    };

    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        {/* Header */}
        <div className="border-b border-slate-200 pb-4 mb-4">
          <h3 className="text-xl font-bold text-slate-900">{pi.full_name || 'Applicant'}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
            {pi.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4 flex-shrink-0" /> {pi.city}{pi.state ? `, ${pi.state}` : ''}
              </span>
            )}
            {pi.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}
              </span>
            )}
            {pi.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}
              </span>
            )}
          </div>
        </div>

        {resume.professional_summary && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Summary</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{resume.professional_summary}</p>
          </div>
        )}

        {coreComps.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Competencies</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{coreComps.join(' \u2022 ')}</p>
          </div>
        )}

        {(() => {
          const wa = resume.military_roles?.find(r => r.mos_data?.work_activities?.length > 0)?.mos_data?.work_activities;
          if (!wa || wa.length === 0) return null;
          return (
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Responsibilities</h4>
              <ul className="space-y-0.5">
                {wa.slice(0, 6).map((a, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{a.name}</span></li>
                ))}
              </ul>
            </div>
          );
        })()}

        {(() => {
          const tt = resume.military_roles?.find(r => r.mos_data?.tools_technology?.length > 0)?.mos_data?.tools_technology;
          if (!tt || tt.length === 0) return null;
          return (
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tools & Systems</h4>
              <p className="text-sm text-slate-700 leading-relaxed">{tt.slice(0, 6).map(t => t.category).join(' \u2022 ')}</p>
            </div>
          );
        })()}

        {resume.military_roles?.length > 0 && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Military Experience</h4>
            {resume.military_roles.map((role, idx) => {
              const bullets = parseBullets(role.ai_summary_text);
              return (
                <div key={idx} className="mb-3 pl-3 border-l-2 border-brand">
                  <p className="font-medium text-slate-900">{role.rank}, {role.branch}</p>
                  <p className="text-sm text-slate-600">{role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}</p>
                  <p className="text-xs text-slate-400 font-mono">{role.location} | {role.start_month}/{role.start_year} - {role.end_month}/{role.end_year}</p>
                  {bullets.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{b}</span></li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {resume.civilian_experience?.some(e => (e.job_title || '').trim() || (e.company || '').trim() || (e.description || '').trim()) && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Experience</h4>
            {resume.civilian_experience.map((exp, idx) => (
              <div key={idx} className="mb-3">
                <p className="font-medium text-slate-900">{exp.job_title}</p>
                <p className="text-sm text-slate-600">{exp.company} - {exp.location}</p>
                <p className="text-xs text-slate-400 font-mono">{exp.start_month}/{exp.start_year} - {exp.is_current ? 'Present' : `${exp.end_month}/${exp.end_year}`}</p>
                {exp.description && <p className="mt-1 text-sm text-slate-700">{exp.description}</p>}
              </div>
            ))}
          </div>
        )}

        {resume.education?.some(e => (e.degree_or_cert || '').trim() || (e.institution || '').trim()) && (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Education</h4>
            {resume.education.map((edu, idx) => (
              <div key={idx} className="mb-2">
                <p className="font-medium text-slate-900">{edu.degree_or_cert}</p>
                <p className="text-sm text-slate-500">{edu.institution}{edu.end_year ? ` (${edu.end_year})` : ''}</p>
              </div>
            ))}
          </div>
        )}

        <AwardsDisplay resume={resume} />
      </div>
    );
  };

  // Render Cover Letter Preview - with stationery-quality sender header matching resume
  const renderCoverLetterPreview = () => {
    if (!applicationDetails?.has_cover_letter || !applicationDetails?.cover_letter_text) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="w-16 h-16 text-slate-300 mb-4" />
          <p className="text-slate-600 font-medium">Cover letter not provided</p>
          <p className="text-sm text-slate-500 mt-1">The candidate did not generate a cover letter</p>
        </div>
      );
    }

    // Get personal info from resume_data (same source as resume header)
    const pi = applicationDetails.resume_data?.personal_info || {};
    
    // Get current date for letter date
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Process cover letter text to remove any embedded date at the start
    const processedText = applicationDetails.cover_letter_text.replace(
      /^(?:(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4}|\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})\s*\n*/i,
      ''
    ).trim();

    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        {/* Sender Header - Matches resume header styling */}
        <div className="border-b border-slate-200 pb-4 mb-4">
          <h3 className="text-xl font-bold text-slate-900">{pi.full_name || applicationDetails.applicant_name || 'Applicant'}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
            {(pi.city || pi.state) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4 flex-shrink-0" /> 
                {[pi.city, pi.state].filter(Boolean).join(', ')}
              </span>
            )}
            {pi.email && (
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}
              </span>
            )}
            {pi.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}
              </span>
            )}
          </div>
        </div>
        
        {/* Date */}
        <p className="text-sm text-slate-600 mb-4">{currentDate}</p>
        
        {/* Cover Letter Body */}
        <div className="space-y-4">
          {processedText.split('\n\n').map((para, idx) => (
            <p key={idx} className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {para.trim()}
            </p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ===== Regional hero (left: identity + actions) + Latest local news (right) ===== */}
        {(() => {
          const regionName = branding?.name || regionalContact?.region_name || 'Your Region';
          const tagline = branding?.tagline || branding?.subtitle;
          const featured = latestNews[0];
          return (
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6" data-testid="regional-hero">
              {/* LEFT — regional identity + primary actions */}
              <div className={`relative overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 flex flex-col justify-center ${featured ? 'lg:col-span-7' : 'lg:col-span-12'}`}>
                <span className="absolute top-0 left-0 right-0 h-1 bg-brand" />
                <p className="text-xs font-bold tracking-wider uppercase text-slate-500 mb-2" data-testid="hero-eyebrow">{regionName}</p>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
                  {regionName} Workforce Network
                </h1>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-3 max-w-2xl">
                  {tagline || 'Connecting local employers with military-connected talent ready to build careers in our region.'}
                </p>
                <div className="flex flex-wrap gap-2.5 mt-6">
                  <Button data-testid="hero-find-talent" onClick={() => navigate('/candidates/search')}
                    className="bg-brand hover:bg-brand-hover text-white h-10 px-5 rounded-lg font-semibold">
                    <Search className="w-4 h-4 mr-2" /> Find Talent
                  </Button>
                  <Button data-testid="hero-post-role" onClick={() => navigate('/jobs/post')} variant="outline"
                    className="border-brand/40 text-brand hover:bg-brand-light h-10 px-5 rounded-lg font-semibold">
                    <Plus className="w-4 h-4 mr-2" /> Post a Role
                  </Button>
                </div>
              </div>

              {/* RIGHT — most recent regional news (editorial). Omitted cleanly when none. */}
              {featured && (
                <button onClick={() => navigate(`/employer/news/${featured.id}`)} data-testid="latest-regional-news-card"
                  className="group text-left lg:col-span-5 bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col hover:border-brand/40 hover:shadow-md transition-all">
                  <span className="self-start bg-brand-light text-brand text-[11px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-md mb-3">Latest from {regionName}</span>
                  {featured.featured_image && (
                    <div className="relative aspect-[16/9] w-full rounded-lg overflow-hidden mb-4 bg-slate-100 border border-slate-100">
                      <img src={featured.featured_image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-brand transition-colors leading-snug line-clamp-2">{featured.title}</h3>
                  {featured.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-3 leading-relaxed">{featured.excerpt}</p>}
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-3 mt-auto border-t border-slate-100">
                    <span>{fmtDate(featured.published_at)}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-brand">Read story <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                  </div>
                </button>
              )}
            </section>
          );
        })()}

        {/* ===== Compact metric strip (real employer metrics) ===== */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-2 mb-6 grid grid-cols-2 md:grid-cols-4 gap-1.5" data-testid="regional-metric-strip">
          {[
            { label: 'Open Roles', value: activeJobs.length, icon: Briefcase, onClick: () => setParams({ tab: 'jobs' }) },
            { label: 'Applications Received', value: allApps.length, icon: Users, onClick: () => setParams({ tab: 'applications' }) },
            { label: 'Saved Talent', value: savedCandidates.length, icon: Heart, onClick: () => setParams({ tab: 'candidates' }) },
            { label: 'Conversations', value: conversationsCount, icon: MessageSquare, onClick: () => navigate('/messages') },
          ].map((m) => {
            const active = m.value > 0;
            const Icon = m.icon;
            return (
              <button key={m.label} onClick={m.onClick} data-testid={`metric-${m.label.toLowerCase().split(' ')[0]}`}
                className="group flex items-center gap-3 text-left px-3.5 py-2.5 rounded-lg hover:bg-slate-50 transition-colors">
                <span className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${active ? 'bg-brand-light text-brand' : 'bg-slate-100 text-slate-400'}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex flex-col min-w-0 leading-none">
                  <span className={`text-2xl font-extrabold tracking-tight tabular-nums ${active ? 'text-slate-900' : 'text-slate-400'}`}>{m.value}</span>
                  <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide truncate mt-1 group-hover:text-slate-700 transition-colors">{m.label}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* ===== Your Hiring Activity ===== */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Your Hiring Activity</h2>
          {jobs.length > 0 && (
            <button onClick={() => setParams({ tab: 'jobs' })} data-testid="hiring-viewall"
              className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1">View all jobs <ArrowRight className="w-3.5 h-3.5" /></button>
          )}
        </div>
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setParams(v === 'jobs' ? {} : { tab: v })} className="w-full">
          <TabsList className="mb-6 bg-slate-100/80 rounded-lg p-1 flex w-full max-w-full min-h-9 h-auto overflow-x-auto overflow-y-hidden justify-start">
            <TabsTrigger value="jobs" className="flex-shrink-0 whitespace-nowrap flex items-center gap-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm px-4">
              <Briefcase className="w-3.5 h-3.5" /> Your roles
              {activeJobs.length > 0 && <span className="text-[10px] bg-brand/10 text-brand font-semibold px-1.5 py-0.5 rounded-full">{activeJobs.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="applications" className="flex-shrink-0 whitespace-nowrap flex items-center gap-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm px-4">
              <FileText className="w-3.5 h-3.5" /> Applications
              {allApps.length > 0 && <span className="text-[10px] bg-brand/10 text-brand font-semibold px-1.5 py-0.5 rounded-full" data-testid="applications-tab-count">{allApps.length}</span>}
              {needsResponseCount > 0 && <span className="text-[10px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded-full" data-testid="applications-tab-needs-response" title="Applications awaiting your response">{needsResponseCount} need response</span>}
            </TabsTrigger>
            <TabsTrigger value="candidates" className="flex-shrink-0 whitespace-nowrap flex items-center gap-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm px-4">
              <Heart className="w-3.5 h-3.5" /> Saved
              {savedCandidates.length > 0 && <span className="text-[10px] bg-violet-100 text-violet-700 font-semibold px-1.5 py-0.5 rounded-full">{savedCandidates.length}</span>}
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            {jobs.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-slate-700 font-medium mb-1">No job postings yet</p>
                <p className="text-sm text-slate-400 mb-5">Create your first position to start receiving applications from qualified veterans</p>
                <Button onClick={() => navigate('/jobs/post')} className="bg-brand hover:bg-brand-hover text-white rounded-lg">
                  <Plus className="w-4 h-4 mr-2" /> Post Your First Job
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map(job => {
                  const appCount = appCountByJob[job.job_id] || 0;
                  const isActive = job.status === 'active';
                  return (
                    <div
                      key={job.job_id}
                      className="group bg-white rounded-xl border border-slate-200 hover:border-brand/40 hover:shadow-md transition-all p-5 cursor-pointer"
                      onClick={() => navigate(`/jobs/${job.job_id}`)}
                      data-testid={`job-card-${job.job_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5 mb-1">
                            <h3 className="text-lg font-semibold text-slate-900 truncate group-hover:text-brand transition-colors">{job.title}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                              isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {job.remote ? 'Remote' : job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              {job.job_type?.replace('_', ' ')}
                            </span>
                            {job.salary_min && (
                              <span className="font-medium text-slate-700">
                                ${(job.salary_min / 1000).toFixed(0)}k{job.salary_max ? `\u2013$${(job.salary_max / 1000).toFixed(0)}k` : '+'}
                              </span>
                            )}
                            {job.posted_at && (
                              <span className="flex items-center gap-1 text-xs text-slate-400">
                                <Clock className="w-3 h-3" />
                                Posted {new Date(job.posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                          {appCount > 0 && (
                            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {appCount} application{appCount !== 1 ? 's' : ''} received
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.job_id}`); }}
                          variant="outline" size="sm"
                          className="border-slate-200 text-slate-700 hover:border-brand hover:text-brand rounded-lg flex-shrink-0"
                        >
                          Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            {!jobFilter && !stageFilter && allApps.length > 0 && (
              <div className="flex items-center gap-x-2 gap-y-1 flex-wrap text-sm text-slate-500 mb-3" data-testid="applications-summary-line">
                <span className="font-semibold text-slate-900" data-testid="applications-summary-total">{allApps.length} application{allApps.length === 1 ? '' : 's'}</span>
                <span className="text-slate-300">·</span>
                <span data-testid="applications-summary-applicants">{uniqueApplicantCount} applicant{uniqueApplicantCount === 1 ? '' : 's'}</span>
                {needsResponseCount > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="applications-summary-needs-response">{needsResponseCount} need response</span>
                  </>
                )}
              </div>
            )}
            {(jobFilter || stageFilter) && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-brand-light/50 border border-brand/20 rounded-xl p-3 mb-3" data-testid="applications-scope-banner">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-sm font-semibold text-slate-900 truncate" data-testid="applications-scope-title">
                    {jobFilter ? `Applicants for ${scopedJobTitle}` : 'All applicants'}
                  </span>
                  {stageFilter && STAGE_LABELS[stageFilter] && (
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-brand bg-white border border-brand/20 rounded-full px-2 py-0.5" data-testid="applications-scope-stage">
                      {STAGE_LABELS[stageFilter]}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearScope}
                  data-testid="applications-clear-scope-btn"
                  className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1 flex-shrink-0"
                >
                  View all applications <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {applications.length === 0 && !appQuery ? (
              <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-slate-700 font-medium mb-1">No applications yet</p>
                <p className="text-sm text-slate-400 mb-5">Applications to your job postings will appear here</p>
                {activeJobs.length > 0 ? (
                  <Button variant="outline" onClick={() => navigate('/candidates/search')} className="border-brand text-brand hover:bg-brand hover:text-white rounded-lg">
                    <Search className="w-4 h-4 mr-2" /> Search Candidates
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/jobs/post')} className="bg-brand hover:bg-brand-hover text-white rounded-lg">
                    <Plus className="w-4 h-4 mr-2" /> Post a Job
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Applicant search + sort controls */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between bg-white rounded-xl border border-slate-200 p-3" data-testid="applications-controls">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="applications-search-input"
                      type="text"
                      value={appQuery}
                      onChange={(e) => { setAppQuery(e.target.value); fetchApplications(appSort, e.target.value, jobFilter, stageFilter); }}
                      placeholder="Search by candidate or job title…"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/30"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Sort</span>
                    <select
                      data-testid="applications-sort-select"
                      value={appSort}
                      onChange={(e) => { setAppSort(e.target.value); fetchApplications(e.target.value, appQuery, jobFilter, stageFilter); }}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 bg-white"
                    >
                      <option value="action_needed">Action Needed</option>
                      <option value="newest">Newest Application</option>
                      <option value="last_activity">Last Activity</option>
                      <option value="stage">Funnel Stage</option>
                    </select>
                  </div>
                </div>
                {applications.length === 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="applications-no-match">
                    No applications match your search.
                  </div>
                )}
                {applications.map(app => {
                  const EMP_STAGE = { started: 'Applied', submitted: 'Applied', contacted: 'Reviewing',
                    interview: 'Interviewing', offer: 'Offer', hired: 'Hired',
                    not_selected: 'Not Selected', withdrawn: 'Withdrawn' };
                  const state = app.current_lifecycle_state || (app.hired ? 'hired' : 'submitted');
                  const stageText = EMP_STAGE[state] || 'Applied';
                  const isTerminal = ['hired', 'not_selected', 'withdrawn'].includes(state);
                  const activityIso = app.last_activity_at || app.applied_at;
                  const days = activityIso ? Math.floor((Date.now() - new Date(activityIso).getTime()) / 86400000) : null;
                  const aging = app.needs_response
                    ? (days == null || days <= 0 ? 'New' : `${days} day${days === 1 ? '' : 's'} waiting`)
                    : null;
                  return (
                  <div key={app.application_id} className={`bg-white rounded-xl border transition-all p-5 ${app.needs_response ? 'border-amber-300 ring-1 ring-amber-100' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`application-${app.application_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                          <h3 className="text-base font-semibold text-slate-900">
                            {app.applicant_name || 'Anonymous Applicant'}
                          </h3>
                          {app.needs_response ? (
                            <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[11px] font-semibold border border-amber-200" data-testid={`needs-response-${app.application_id}`}>
                              Needs Response
                            </span>
                          ) : (
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${state === 'hired' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : state === 'not_selected' || state === 'withdrawn' ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-brand-light text-brand border-brand/20'}`} data-testid={`stage-${app.application_id}`}>
                              {stageText}
                            </span>
                          )}
                          {aging && (
                            <span className="text-[11px] text-amber-600 font-medium" data-testid={`aging-${app.application_id}`}>{aging}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          Applied for <span className="font-medium text-slate-700">{app.job_title}</span>
                          <span className="mx-1.5 text-slate-300">|</span>
                          {new Date(app.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        {/* Native-job disposition controls (workflow strongly encouraged, not blocking) */}
                        {app.can_disposition && !isTerminal && (
                          <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid={`dispositions-${app.application_id}`}>
                            {state !== 'contacted' && state !== 'interview' && state !== 'offer' && (
                              <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 rounded-lg"
                                disabled={!!dispatching} data-testid={`disposition-reviewing-${app.application_id}`}
                                onClick={() => handleDisposition(app.application_id, 'reviewing')}>Reviewing</Button>
                            )}
                            {state !== 'interview' && state !== 'offer' && (
                              <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 rounded-lg"
                                disabled={!!dispatching} data-testid={`disposition-interview-${app.application_id}`}
                                onClick={() => handleDisposition(app.application_id, 'interview')}>Interview</Button>
                            )}
                            {state !== 'offer' && (
                              <Button size="sm" variant="outline" className="h-8 text-xs border-slate-200 rounded-lg"
                                disabled={!!dispatching} data-testid={`disposition-offer-${app.application_id}`}
                                onClick={() => handleDisposition(app.application_id, 'offer')}>Offer</Button>
                            )}
                            <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                              disabled={!!dispatching} data-testid={`disposition-hired-${app.application_id}`}
                              onClick={() => handleDisposition(app.application_id, 'hired')}>Hire</Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500 hover:text-slate-700 rounded-lg"
                              disabled={!!dispatching} data-testid={`disposition-not-selected-${app.application_id}`}
                              onClick={() => handleDisposition(app.application_id, 'not_selected')}>Not Selected</Button>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          data-testid={`view-application-btn-${app.application_id}`}
                          onClick={() => {
                            if (jobFilter || stageFilter) {
                              const p = new URLSearchParams({ origin: 'apps' });
                              if (jobFilter) p.set('job', jobFilter);
                              if (stageFilter) p.set('stage', stageFilter);
                              navigate(`/applications/${app.application_id}?${p.toString()}`);
                            } else {
                              navigate(`/applications/${app.application_id}`);
                            }
                          }}
                          className="bg-brand hover:bg-brand-hover text-white rounded-lg" size="sm"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1.5" /> Open Application
                        </Button>
                        <Button data-testid={`message-btn-${app.application_id}`} onClick={() => navigate(`/messages?application=${app.application_id}`)} variant="outline" size="sm" className="border-slate-200 rounded-lg">
                          <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Message
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="candidates">
            {savedCandidates.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-16 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-7 h-7 text-slate-300" />
                </div>
                <p className="text-slate-700 font-medium mb-1">No saved candidates yet</p>
                <p className="text-sm text-slate-400 mb-5">Favorite candidates from the search page to see them here</p>
                <Button onClick={() => navigate('/candidates/search')} className="bg-brand hover:bg-brand-hover text-white rounded-lg">
                  <Search className="w-4 h-4 mr-2" /> Search Candidates
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {savedCandidates.map((candidate, index) => (
                  <div key={candidate.user_id || index} className="bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all p-5" data-testid={`saved-candidate-${candidate.user_id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-semibold text-slate-900 mb-0.5">
                          {candidate.personal_info?.full_name || 'Anonymous'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                          {candidate.personal_info?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {candidate.personal_info.city}{candidate.personal_info.state ? `, ${candidate.personal_info.state}` : ''}
                            </span>
                          )}
                          {candidate.military_roles?.[0] && (
                            <span>{candidate.military_roles[0].branch} · {sanitizeMosTitle(candidate.military_roles[0].mos_title, candidate.military_roles[0].rank)}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnfavorite(candidate.user_id)}
                        className="p-1.5 hover:bg-red-50 rounded-full transition-colors flex-shrink-0"
                        data-testid={`unfavorite-btn-${candidate.user_id}`}
                        aria-label="Remove from saved"
                      >
                        <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                      </button>
                    </div>
                    <AwardsDisplay resume={candidate} compact />
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <Button
                        onClick={() => handleSavedViewProfile(candidate)}
                        disabled={checkingAccess === candidate.user_id}
                        variant="outline" size="sm" className="border-brand/30 text-brand hover:bg-brand-light rounded-lg"
                        data-testid={`saved-view-profile-btn-${candidate.user_id}`}
                      >
                        {checkingAccess === candidate.user_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                        View Profile
                      </Button>
                      <Button onClick={() => navigate(`/messages?candidate=${candidate.user_id}`)} variant="outline" size="sm" className="border-slate-200 text-slate-600 rounded-lg" data-testid={`saved-message-btn-${candidate.user_id}`}>
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Message
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-8 space-y-8">

        {/* ===== More from [Region] — additional regional news, after Hiring Activity, before Tools ===== */}
        {(() => {
          const regionName = branding?.name || regionalContact?.region_name || 'Your Region';
          const moreNews = latestNews.slice(1, 3);
          if (moreNews.length === 0) return null;
          return (
            <section data-testid="dash-more-news">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">More from {regionName}</h2>
                {latestNews.length > 3 && (
                  <button onClick={() => navigate('/employer/news')} data-testid="dash-news-viewall"
                    className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1">View all local news <ArrowRight className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <div className={`grid grid-cols-1 gap-4 ${moreNews.length > 1 ? 'md:grid-cols-2' : ''}`}>
                {moreNews.map((n) => (
                  <button key={n.id} onClick={() => navigate(`/employer/news/${n.id}`)} data-testid={`dash-more-news-${n.id}`}
                    className="group text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:border-brand/40 hover:shadow-md transition-all overflow-hidden flex">
                    {n.featured_image && (
                      <div className="w-28 bg-slate-100 overflow-hidden flex-shrink-0">
                        <img src={n.featured_image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4 min-w-0 flex flex-col justify-center">
                      <p className="text-xs text-slate-400 mb-0.5">{fmtDate(n.published_at)}</p>
                      <h3 className="text-sm font-bold text-slate-900 group-hover:text-brand transition-colors leading-snug line-clamp-2">{n.title}</h3>
                      {n.excerpt && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.excerpt}</p>}
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand mt-1.5">Read story <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></span>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })()}

        {/* ===== Global Workforce Update — single compact banner, secondary to regional ===== */}
        {workforceUpdate && (
          <button onClick={() => navigate(`/employer/article/news/${workforceUpdate.id}`)} data-testid="dash-workforce-update"
            className="group w-full text-left bg-slate-900 text-white rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm hover:bg-slate-800 transition-colors">
            <div className="min-w-0">
              <span className="inline-block bg-brand text-white text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5">Workforce Update</span>
              <h3 className="text-sm sm:text-base font-bold text-white leading-snug line-clamp-1">{workforceUpdate.title}</h3>
              {workforceUpdate.excerpt && <p className="text-xs text-slate-300 mt-0.5 line-clamp-1">{workforceUpdate.excerpt}</p>}
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-light group-hover:text-white underline underline-offset-4 flex-shrink-0">Read update <ArrowRight className="w-3.5 h-3.5" /></span>
          </button>
        )}

        {/* ===== Tools & Support for Your Business ===== */}
        {(() => {
          const regionName = branding?.name || regionalContact?.region_name || 'your region';
          const topResources = resources.slice(0, 3);
          const topGuides = guides.slice(0, 3);
          const contact = regionalContact?.contact;
          const hasGuides = topGuides.length > 0;
          const hasResources = topResources.length > 0;
          const hasContact = !!contact;
          if (!hasGuides && !hasResources && !hasContact) return null;
          return (
            <div data-testid="regional-content">
              {(hasGuides || hasResources || hasContact) && (
                <>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-1">Tools &amp; Support for Your Business</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
                    {/* Employer Guides (global) */}
                    {hasGuides && (
                      <section data-testid="dash-guides">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><BookOpen className="w-4 h-4 text-brand" /> Employer Guides</h3>
                          <button onClick={() => navigate('/employer/resources')} data-testid="dash-guides-viewall"
                            className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></button>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">Practical tools for recruiting and retaining military talent.</p>
                        <div className="space-y-3">
                          {topGuides.map((g) => (
                            <button key={g.id} onClick={() => navigate(`/employer/article/resource/${g.id}`)} data-testid={`dash-guide-${g.id}`}
                              className="group w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:border-brand/40 hover:shadow-md transition-all p-4 flex gap-4 items-start">
                              {g.featured_image && (
                                <div className="w-20 h-16 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                                  <img src={g.featured_image} alt="" className="w-full h-full object-cover" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-bold text-slate-900 group-hover:text-brand transition-colors leading-snug line-clamp-2">{g.title}</h4>
                                {g.excerpt && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{g.excerpt}</p>}
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand mt-1.5">Read guide <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Regional Services (region-owned) + Workforce Contact */}
                    {(hasResources || hasContact) && (
                      <section>
                        {hasResources && (
                          <>
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-brand" /> Regional Services</h3>
                              <button onClick={() => navigate('/employer/resources?tab=services')} data-testid="dash-resources-viewall"
                                className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></button>
                            </div>
                            <p className="text-xs text-slate-500 mb-3">Local workforce services available through {regionName}.</p>
                            <div className="space-y-3">
                              {topResources.map((r) => (
                                <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`dash-resource-${r.id}`}
                                  className="group block bg-white rounded-xl border border-slate-200 shadow-sm hover:border-brand/40 hover:shadow-md transition-all p-4">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      {r.category && <span className="text-[10px] font-semibold uppercase tracking-wide bg-brand-light text-brand px-1.5 py-0.5 rounded mb-1 inline-block">{r.category}</span>}
                                      <h4 className="text-sm font-bold text-slate-900 group-hover:text-brand transition-colors truncate">{r.title}</h4>
                                      {r.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{toPreviewText(r.description)}</p>}
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-brand flex-shrink-0 mt-0.5" />
                                  </div>
                                </a>
                              ))}
                            </div>
                          </>
                        )}

                        {hasContact && (
                          <div data-testid="workforce-contact-block" className="mt-4 bg-brand-light/60 border border-brand/20 rounded-xl p-4 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="w-10 h-10 rounded-full bg-brand text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                                {(contact.name || 'W').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                              </span>
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-brand">Your Workforce Contact</p>
                                {contact.name && <p className="text-sm font-semibold text-slate-900 truncate">{contact.name}</p>}
                                <p className="text-xs text-slate-500 truncate">{regionalContact?.region_name || regionName}</p>
                              </div>
                            </div>
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} data-testid="regional-contact-email"
                                className="inline-flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors flex-shrink-0">
                                <Mail className="w-3.5 h-3.5" /> Contact
                              </a>
                            )}
                          </div>
                        )}
                      </section>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        </div>
      </div>

      {/* Application Viewer Dialog */}
      <Dialog open={!!viewingApplication} onOpenChange={(open) => !open && handleCloseViewer()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="application-viewer-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl font-bold text-slate-900">
              {loadingApplication ? 'Loading...' : `Application: ${applicationDetails?.applicant_name || 'Applicant'}`}
            </DialogTitle>
          </DialogHeader>

          {loadingApplication ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : applicationDetails ? (
            <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
              {/* Application Header */}
              <div className="border-b border-slate-200 pb-4 mb-4 flex-shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-brand" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-slate-900">{applicationDetails.applicant_name}</h3>
                    <p className="text-sm text-slate-600">
                      Applied for <span className="font-medium">{applicationDetails.job_title}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(applicationDetails.applied_at).toLocaleDateString('en-US', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </p>
                  </div>
                  {applicationDetails.hired && (
                    <span className="flex-shrink-0 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Hired
                    </span>
                  )}
                </div>
                
                {/* Personal Message — separate from Resume/Cover Letter */}
                {applicationDetails.application_message && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Personal Message</p>
                    <p className="text-sm text-slate-700">{applicationDetails.application_message}</p>
                  </div>
                )}
              </div>

              {/* Document Tabs */}
              <div className="flex-shrink-0 flex border-b border-slate-200 mb-4">
                <button
                  onClick={() => setActiveDocTab('resume')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeDocTab === 'resume' 
                      ? 'border-brand text-brand' 
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Resume
                </button>
                <button
                  onClick={() => setActiveDocTab('cover')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeDocTab === 'cover' 
                      ? 'border-brand text-brand' 
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Cover Letter
                </button>
              </div>

              {/* Document Actions */}
              <div className="flex-shrink-0 flex items-center gap-2 mb-4">
                {activeDocTab === 'resume' ? (
                  <>
                    <Button
                      onClick={handleDownloadResumePdf}
                      disabled={!applicationDetails.has_resume || downloadingResumePdf}
                      size="sm"
                      className="bg-brand hover:bg-brand-hover text-white"
                      data-testid="download-applicant-resume-pdf"
                    >
                      {downloadingResumePdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      PDF
                    </Button>
                    <Button
                      onClick={handlePrintResume}
                      disabled={!applicationDetails.has_resume}
                      size="sm"
                      variant="outline"
                      data-testid="print-applicant-resume"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={handleDownloadCoverPdf}
                      disabled={!applicationDetails.has_cover_letter || downloadingCoverPdf}
                      size="sm"
                      className="bg-brand hover:bg-brand-hover text-white"
                      data-testid="download-applicant-cover-pdf"
                    >
                      {downloadingCoverPdf ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                      PDF
                    </Button>
                    <Button
                      onClick={handlePrintCover}
                      disabled={!applicationDetails.has_cover_letter}
                      size="sm"
                      variant="outline"
                      data-testid="print-applicant-cover"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                  </>
                )}
              </div>

              {/* Document Preview - Single scroll container */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {activeDocTab === 'resume' ? renderResumePreview() : renderCoverLetterPreview()}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Saved Candidate — Resume Viewer Dialog */}
      <Dialog open={savedResumeViewerOpen} onOpenChange={(open) => { if (!open) { setSavedResumeViewerOpen(false); setSavedResumeData(null); setSavedResumeCandidate(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="saved-resume-viewer-dialog">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <FileText className="w-5 h-5 text-brand" />
              {savedResumeCandidate?.personal_info?.full_name || 'Candidate'} — Application Documents
            </DialogTitle>
          </DialogHeader>

          {loadingSavedResume ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
          ) : savedResumeData ? (
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {/* Profile-level access shows the canonical resume/profile only.
                  Generic cover letters are never exposed at the profile level (Prompt 4). */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                <SavedResumePreview resume={savedResumeData} />
              </div>
            </div>
          ) : (
            <div className="py-12 text-center text-slate-500">Resume data not available.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Saved Candidate — Access Request Dialog */}
      <Dialog open={savedAccessRequestOpen} onOpenChange={(open) => { if (!open) { setSavedAccessRequestOpen(false); setSavedAccessNote(''); setSavedAccessCandidate(null); } }}>
        <DialogContent className="max-w-lg" data-testid="saved-access-request-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Eye className="w-5 h-5 text-brand" />
              Request Full Profile Access
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {savedAccessCandidate && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-brand" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">{savedAccessCandidate.personal_info?.full_name || 'Candidate'}</h4>
                    {savedAccessCandidate.personal_info?.city && (
                      <p className="text-xs text-slate-500">{savedAccessCandidate.personal_info.city}, {savedAccessCandidate.personal_info.state}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-900 font-medium text-sm">
                Note to Candidate <span className="text-slate-400">(optional)</span>
              </Label>
              <Textarea
                data-testid="saved-access-request-note"
                value={savedAccessNote}
                onChange={(e) => setSavedAccessNote(e.target.value)}
                className="mt-2 min-h-[80px] text-sm"
                placeholder="Introduce yourself or explain why you'd like to view their full profile..."
              />
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button
                data-testid="cancel-saved-access-request-btn"
                onClick={() => { setSavedAccessRequestOpen(false); setSavedAccessNote(''); setSavedAccessCandidate(null); }}
                variant="outline" size="sm" className="border-slate-300 text-slate-700"
              >
                Cancel
              </Button>
              <Button
                data-testid="send-saved-access-request-btn"
                onClick={handleSendSavedAccessRequest}
                disabled={sendingSavedRequest}
                size="sm"
                className="bg-brand hover:bg-brand-hover text-white"
              >
                {sendingSavedRequest ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Request Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/** Inline resume preview for Saved Candidates viewer */
function SavedResumePreview({ resume }) {
  const pi = resume.personal_info || {};

  const coreCompetencies = (() => {
    if (!resume.military_roles?.length) return [];
    const all = [];
    for (const role of resume.military_roles) {
      for (const item of (role.selected_abilities || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
      for (const item of (role.selected_skills || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
      for (const item of (role.selected_knowledge || [])) { const n = typeof item === 'object' ? item.element_name : item; if (n && !all.includes(n)) all.push(n); }
    }
    const seen = new Set();
    return all.filter(c => { const l = c.toLowerCase().trim(); if (seen.has(l)) return false; seen.add(l); return true; }).slice(0, 15);
  })();

  const parseBullets = (text) => {
    if (!text) return [];
    const lower = text.toLowerCase();
    if (lower.includes('<!doctype') || lower.includes('<html') || lower.includes('ratelimiterror') ||
        lower.includes('rate_limit_exceeded') || (text.match(/<[a-zA-Z/][^>]*>/g) || []).length > 3) {
      return [];
    }
    return text.split('\n').map(l => l.trim()).filter(l => l && !l.startsWith('#')).map(l => l.replace(/^[•\-\*▪◦●]\s*/, ''));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="saved-resume-preview-content">
      <div className="border-b border-slate-200 pb-4 mb-4">
        <h3 className="text-xl font-bold text-slate-900">{pi.full_name || 'Candidate'}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600">
          {pi.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4 flex-shrink-0" /> {pi.city}{pi.state ? `, ${pi.state}` : ''}</span>}
          {pi.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4 flex-shrink-0" /> {pi.email}</span>}
          {pi.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4 flex-shrink-0" /> {pi.phone}</span>}
        </div>
      </div>
      {resume.professional_summary && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Summary</h4>
          <p className="text-sm text-slate-700 leading-relaxed">{resume.professional_summary}</p>
        </div>
      )}
      {coreCompetencies.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Competencies</h4>
          <p className="text-sm text-slate-700 leading-relaxed">{coreCompetencies.join(' \u2022 ')}</p>
        </div>
      )}
      {(() => {
        const wa = resume.military_roles?.find(r => r.mos_data?.work_activities?.length > 0)?.mos_data?.work_activities;
        if (!wa || wa.length === 0) return null;
        return (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Core Responsibilities</h4>
            <ul className="space-y-0.5">
              {wa.slice(0, 6).map((a, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{a.name}</span></li>
              ))}
            </ul>
          </div>
        );
      })()}
      {(() => {
        const tt = resume.military_roles?.find(r => r.mos_data?.tools_technology?.length > 0)?.mos_data?.tools_technology;
        if (!tt || tt.length === 0) return null;
        return (
          <div className="mb-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tools & Systems</h4>
            <p className="text-sm text-slate-700 leading-relaxed">{tt.slice(0, 6).map(t => t.category).join(' \u2022 ')}</p>
          </div>
        );
      })()}
      {resume.military_roles?.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Military Experience</h4>
          {resume.military_roles.map((role, idx) => {
            const bullets = parseBullets(role.ai_summary_text);
            return (
              <div key={idx} className="mb-3 pl-3 border-l-2 border-brand">
                <p className="font-medium text-slate-900">{role.rank}, {role.branch}</p>
                <p className="text-sm text-slate-600">{role.mos_code} - {sanitizeMosTitle(role.mos_title, role.rank)}</p>
                <p className="text-xs text-slate-400 font-mono">{role.location} | {role.start_month}/{role.start_year} - {role.end_month}/{role.end_year}</p>
                {bullets.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {bullets.map((b, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-slate-400 mt-0.5">&bull;</span><span>{b}</span></li>)}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
      {resume.civilian_experience?.some(e => (e.job_title || '').trim() || (e.company || '').trim() || (e.description || '').trim()) && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Professional Experience</h4>
          {resume.civilian_experience.map((exp, idx) => (
            <div key={idx} className="mb-3">
              <p className="font-medium text-slate-900">{exp.job_title}</p>
              <p className="text-sm text-slate-600">{exp.company} - {exp.location}</p>
              <p className="text-xs text-slate-400 font-mono">{exp.start_month}/{exp.start_year} - {exp.is_current ? 'Present' : `${exp.end_month}/${exp.end_year}`}</p>
              {exp.description && <p className="mt-1 text-sm text-slate-700">{exp.description}</p>}
            </div>
          ))}
        </div>
      )}
      {resume.education?.some(e => (e.degree_or_cert || '').trim() || (e.institution || '').trim()) && (
        <div className="mb-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Education</h4>
          {resume.education.map((edu, idx) => (
            <div key={idx} className="mb-2">
              <p className="font-medium text-slate-900">{edu.degree_or_cert}</p>
              <p className="text-sm text-slate-500">{edu.institution}{edu.end_year ? ` (${edu.end_year})` : ''}</p>
            </div>
          ))}
        </div>
      )}
      <AwardsDisplay resume={resume} />
    </div>
  );
}

export default EmployerDashboard;
